import React from 'react';
import { useCookies } from 'react-cookie';
import { UserType } from '../types';
import { useApi } from '../hooks/useApi';
import { isExpired, decodeToken } from 'react-jwt';

type LoginForm = {
  email: string;
  password: string;
}

type AuthUser = {
  userId: number;
  userNo: number;
  userType: UserType;
  exp: number;
}

type ApiAuthUser = {
  user_id: number;
  user_no: number;
  user_type: UserType;
  exp: number;
}

type Tokens = {
  access_token: string;
  refresh_token: string;
}

export type AuthUserContextType = {
  user: AuthUser | null;
  signin: (loginForm: LoginForm, onSuccess: () => void, onError: () => void) => void;
  external_signin: (params: Record<string, string | number | undefined | string[] | boolean>, onSuccess: () => void, onError: () => void) => void;
  signout: (callback: () => void) => void;
}

const AuthUserContext = React.createContext<AuthUserContextType>({} as AuthUserContextType);

export const useAuthUserContext = (): AuthUserContextType => {
  return React.useContext<AuthUserContextType>(AuthUserContext);
}

type Props = {
  children: React.ReactNode
}

  const castUserFromToken = (accessToken: string): AuthUser => {
    const decoded = decodeToken(accessToken) as ApiAuthUser;
    return {
      userId: decoded.user_id,
      userNo: decoded.user_no,
      userType: decoded.user_type,
      exp: decoded.exp
    }
  }


export const AuthUserProvider = (props: Props) => {
  const [ cookies, setCookie, removeCookie ] = useCookies(['access_token', 'refresh_token']);
  const [ user, setUser ] = React.useState<AuthUser | null>(() => {
    const accessToken = cookies.access_token;
    if (accessToken && !isExpired(accessToken)) {
      return castUserFromToken(cookies.access_token || '');
    } else {
      removeCookie('access_token', { path: '/' });
      removeCookie('refresh_token', { path: '/' });
      return null;
    }
  });
  const { post } = useApi();

  const signin = React.useCallback((loginForm: LoginForm, onSuccess: () => void, onError: () => void) => {
    post({
      endpoint: `/api/v1/login`,
      skipAuth: true,
      body: loginForm,
      onSuccess: (res) => {
        const tokens: Tokens = res?.data;
        if (tokens?.access_token) {
          const user = castUserFromToken(tokens.access_token);
          const refresh = castUserFromToken(tokens.refresh_token);
          setCookie('access_token', tokens.access_token || '', { path: '/', expires: new Date(user.exp * 1000) });
          setCookie('refresh_token', tokens.refresh_token || '', { path: '/', expires: new Date(refresh.exp * 1000) });
          setUser(user);
        }
        onSuccess();
      },
      onError: (err) => {
        console.error(err.message)
        removeCookie('access_token', { path: '/' });
        removeCookie('refresh_token', { path: '/' });
        setUser(null);
        onError();
      },
    });
  }, [ post, setCookie, removeCookie ]);

  const external_signin = React.useCallback((params: Record<string, string | number | undefined | string[] | boolean>, onSuccess: () => void, onError: () => void) => {
    post({
      endpoint: `/api/v1/external_login`,
      skipAuth: true,
      body: params,
      onSuccess: (res) => {
        const tokens: Tokens = res?.data;
        if (tokens?.access_token) {
          const user = castUserFromToken(tokens.access_token);
          const refresh = castUserFromToken(tokens.refresh_token);
          setCookie('access_token', tokens.access_token || '', { path: '/', expires: new Date(user.exp * 1000) });
          setCookie('refresh_token', tokens.refresh_token || '', { path: '/', expires: new Date(refresh.exp * 1000) });
          setUser(user);
        }
        onSuccess();
      },
      onError: (err) => {
        console.error(err.message)
        removeCookie('access_token', { path: '/' });
        removeCookie('refresh_token', { path: '/' });
        setUser(null);
        onError();
      },
    });
  }, [ post, setCookie, removeCookie ]);

  const signout = React.useCallback((callback: () => void) => {
    post({
      endpoint: `/api/v1/users/${ user?.userId }/logout`,
      method: 'DELETE',
      onError: (err) => {
        console.error(err.message);
      },
      onFinally: () => {
        console.log('signout completed');
        setUser(null);
        removeCookie('access_token', { path: '/' });
        removeCookie('refresh_token', { path: '/' });
        callback();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ post, user?.userId ]);

  const value: AuthUserContextType = React.useMemo(
    () => ({ user, signin, external_signin, signout }),
    [ user, signin, external_signin, signout ]
  );

  return (
    <AuthUserContext.Provider value={ value }>
      { props.children }
    </AuthUserContext.Provider>
  );
}
